import { useCallback, useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import fetchJSON from "common/utils/fetchJSON";
import { parseStrapiFormat } from "common/utils/strapi";
import { Balance } from "types/Balance";
import { BalanceSheetFilters } from "types/SavedBalanceSheet";

const toIsoString = (value: any): string => {
  if (!value) {
    return "";
  }
  const date = dayjs(value);
  return date.isValid() ? date.toISOString() : "";
};

/**
 * Normalizes the filter state of the balance sheet page into the shape that is
 * both sent to `GET /balance-sheet/data` and persisted on a saved balance sheet:
 * dates as ISO strings, empty values dropped.
 */
export const buildBalanceSheetFilters = (filters: any): BalanceSheetFilters => {
  const { startDate, endDate, ...rest } = filters ?? {};

  const cleaned: any = {};
  Object.entries(rest).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== "") {
      cleaned[key] = value;
    }
  });

  return {
    ...cleaned,
    startDate: toIsoString(startDate),
    endDate: toIsoString(endDate),
  };
};

// The API only reads inline query params, a JSON payload is not supported.
const buildBalanceSheetQuery = (filters: BalanceSheetFilters) =>
  Object.entries(filters)
    .filter(
      ([, value]) => value !== null && value !== undefined && value !== ""
    )
    .map(
      ([key, value]) => `filters[${key}]=${encodeURIComponent(String(value))}`
    )
    .join("&");

export type UseBalanceSheetDataType = {
  balance?: Balance;
  setBalance: React.Dispatch<React.SetStateAction<Balance | undefined>>;
  isFetching: boolean;
  error: string | null;
  fetchBalance: (filters: any) => Promise<Balance | false>;
};

/**
 * Fetches a balance sheet from its filters. Concurrent calls are safe: the
 * in-flight request is aborted and only the answer of the latest call is kept.
 */
const useBalanceSheetData = (): UseBalanceSheetDataType => {
  const [balance, setBalance] = useState<Balance>();
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const lastRequestIdRef = useRef(0);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const fetchBalance = useCallback(async (filters: any) => {
    const currentRequestId = ++lastRequestIdRef.current;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      setIsFetching(true);
      setError(null);

      const query = buildBalanceSheetQuery(buildBalanceSheetFilters(filters));

      const res = await fetchJSON({
        url: `balance-sheet/data?${query}`,
        method: "GET",
        signal: controller.signal,
      });

      if (res && currentRequestId === lastRequestIdRef.current) {
        setBalance(parseStrapiFormat(res));

        return res;
      }

      return false;
    } catch (e) {
      if ((e as any)?.name === "AbortError") {
        return false;
      }
      console.error(e);
      if (currentRequestId === lastRequestIdRef.current) {
        setError((e as any)?.message ?? null);
      }
      return false;
    } finally {
      if (currentRequestId === lastRequestIdRef.current) {
        setIsFetching(false);
      }
    }
  }, []);

  return { balance, setBalance, isFetching, error, fetchBalance };
};

export default useBalanceSheetData;
